import {Injectable} from '@angular/core';
import {DtrumApi} from '@dynatrace/dtrum-api-types';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {
  CartAddEntryFailEvent,
  CartAddEntrySuccessEvent,
  CartRemoveEntryFailEvent,
  CartRemoveEntrySuccessEvent,
  OrderEntry
} from '@spartacus/cart/base/root';
import {BaseSiteService, CxEvent, LoginEvent, Product, User, WindowRef} from '@spartacus/core';
import {Order, OrderPlacedEvent} from '@spartacus/order/root';
import {
  CategoryPageResultsEvent,
  NavigationEvent,
  ProductDetailsPageEvent,
  SearchPageResultsEvent
} from '@spartacus/storefront';
import {TmsCollector, TmsCollectorConfig, WindowObject} from '@spartacus/tracking/tms/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {DCPErrorEvent} from '@app/core/components/error/dcp-error-events';
import {DCPErrorMessageDisplayEvent, DCPErrorPageDisplayEvent} from '@dcp/configurator';
import {DrumCollectorConfig} from '../config/default-dtrum.config';

/**
 * DCP Dynatrace RUM collector.
 */
@UntilDestroy()
@Injectable({providedIn: 'root'})
export class DtrumCollectorService implements TmsCollector {
  constructor(protected winRef: WindowRef, protected userFacade: UserAccountFacade, private baseSiteService: BaseSiteService) {
  }


  /**
   * If  'scriptUrl', we assume it will be auto-injected by dynatrace
   */
  init(config: DrumCollectorConfig, windowObject: WindowObject): void {
    if (config.enabled && config.scriptUrl && !config.autoInjectedCode) {
      const d = this.winRef.document;
      const g = d.createElement('script') as HTMLScriptElement;
      const script = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = config.scriptUrl;
      script?.parentNode?.insertBefore(g, script);
    }
  }

  pushEvent<T extends CxEvent>(
    config: TmsCollectorConfig,
    w: WindowObject,
    event: T | any
  ): void {
    this.processCommerceEvents(w, event);
  }

  processCommerceEvents?<T extends CxEvent>(w: WindowObject, event: T) {
    const dtrum: DtrumApi = w.dtrum;

    if (dtrum) {
      if (event instanceof LoginEvent) {
        this.addSessionProperties(dtrum);
      } else if (event instanceof NavigationEvent) {

        this.addSessionProperties(dtrum);

        const actionId = dtrum.enterAction('NavigationEvent' + ' ' + event.url);
        dtrum.leaveAction(actionId);
      } else if (event instanceof SearchPageResultsEvent) {

        const actionId = dtrum.enterAction('SearchPageResultsEvent');

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'searchTerm', event.searchTerm);
        DtrumCollectorService.addNumberProperty(dtrum, actionId, 'numberOfResults', event.numberOfResults.valueOf());

        dtrum.leaveAction(actionId);

      } else if (event instanceof ProductDetailsPageEvent) {

        const productCategoryName = event.categories
          ? event.categories[
          event.categories.length - 1
            ].name
          : undefined;

        const actionId = dtrum.enterAction('ProductDetailsPageEvent');

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productCode', event.code);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productName', event.name);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productCategoryName', productCategoryName);

        dtrum.leaveAction(actionId);


      } else if (event instanceof CategoryPageResultsEvent) {

        const actionId = dtrum.enterAction('CategoryPageResultsEvent');

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productCategoryName', event.categoryName);

        dtrum.leaveAction(actionId);

      } else if (event instanceof CartAddEntrySuccessEvent) {

        const productCategoryName = event.entry.product.categories
          ? event.entry.product.categories[
          event.entry.product.categories.length - 1
            ].name
          : undefined;

        const actionId = dtrum.enterAction('CartAddEntrySuccessEvent');

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productCode', event.entry.product.code);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productName', event.entry.product.name);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productCategoryName', productCategoryName);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'itemTotalFormatted', event.entry.totalPrice.formattedValue);
        DtrumCollectorService.addNumberProperty(dtrum, actionId, 'itemQuantityAdded', event.quantityAdded);

        dtrum.leaveAction(actionId);

      } else if (event instanceof CartAddEntryFailEvent) {

        const actionId = dtrum.enterAction('CartAddEntryFailEvent');

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productCode', event.productCode);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'errorMessage', JSON.stringify(event.error));

        dtrum.leaveAction(actionId);

      } else if (event instanceof CartRemoveEntryFailEvent) {

        const actionId = dtrum.enterAction('CartRemoveEntryFailEvent');

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'orderCode', event.entry.orderCode);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'itemNumber', event.entry.entryNumber.toString());

        dtrum.leaveAction(actionId);

      } else if (event instanceof CartRemoveEntrySuccessEvent) {

        const actionId = dtrum.enterAction('CartRemoveEntrySuccessEvent');

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'orderCode', event.entry.orderCode);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'productCode', event.entry.product.code);

        dtrum.leaveAction(actionId);

      } else if (event instanceof DCPErrorEvent) {

        const actionName = 'GENERAL_ERROR';
        DtrumCollectorService.reportError(event, dtrum, actionName);

      } else if (event instanceof DCPErrorPageDisplayEvent) {


        const actionName = 'DISPLAY_ERROR_PAGE';
        DtrumCollectorService.reportError(event, dtrum, actionName);


      } else if (event instanceof DCPErrorMessageDisplayEvent) {

        const actionName = 'DISPLAY_ERROR_MESSAGE';
        DtrumCollectorService.reportError(event, dtrum, actionName);

      } else if (event instanceof OrderPlacedEvent) {

        const order = event as Order;
        const productSupplierAlternativeAIDs = [];
        const productCodes = [];
        order?.entries?.forEach((entry: OrderEntry) => {
          const product = entry.product as Product;
          const supplierAlternativeAID = product.supplierAlternativeAID ? product.supplierAlternativeAID : 'no-supplierAlternativeAID';

          const itemActionId = dtrum.enterAction('OrderItemPlacedEvent');

          DtrumCollectorService.addNumberProperty(dtrum, itemActionId, 'orderTotal', entry.totalPrice.value);

          DtrumCollectorService.addStringProperty(dtrum, itemActionId, 'orderBaseStore', order.store);
          DtrumCollectorService.addStringProperty(dtrum, itemActionId, 'orderCurrency', order.totalPrice.currencyIso);
          DtrumCollectorService.addStringProperty(dtrum, itemActionId, 'productSupplierAlternativeAID', supplierAlternativeAID);
          DtrumCollectorService.addStringProperty(dtrum, itemActionId, 'productCode', product.code);
          DtrumCollectorService.addStringProperty(dtrum, itemActionId, 'orderCode', order.code);

          dtrum.leaveAction(itemActionId);

          productSupplierAlternativeAIDs.push(supplierAlternativeAID);
          productCodes.push(product.code);
        });

        const actionId = dtrum.enterAction('OrderPlacedEvent');

        DtrumCollectorService.addNumberProperty(dtrum, actionId, 'orderTotal', order.totalPrice.value);
        DtrumCollectorService.addNumberProperty(dtrum, actionId, 'orderTotalTax', order.totalTax.value);

        DtrumCollectorService.addStringProperty(dtrum, actionId, 'orderBaseStore', order.store);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'orderCurrency', order.totalPrice.currencyIso);
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'orderProductSupplierAlternativeAIDs', productSupplierAlternativeAIDs.join());
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'orderProductCodes', productCodes.join());
        DtrumCollectorService.addStringProperty(dtrum, actionId, 'orderCode', order.code);

        dtrum.leaveAction(actionId);
      }
    }
  }

  private static reportError<T>(event: T & DCPErrorEvent, dtrum: DtrumApi, actionName: string) {

    if (!event.error) {
      return;
    }

    if (DtrumCollectorService.isScriptError(event)) {

      //In case of script error (e.g. access to undefined, we use DT js error API (supports stacks)
      const dtError: Error = {
        name: event.error?.name,
        message: event.error?.message + ' (' + event.error?.name + ' , severity:' + event.error?.severity + ')',
        stack: event.error?.stack

      };

      dtrum.reportError(dtError);
    } else {
      //In case of non-script error (e.g. validation messages, or http errors) - we use custom errors in dynatrace
      const actionId = dtrum.enterAction(actionName);
      DtrumCollectorService.addStringProperty(dtrum, actionId, 'errorName', event.error?.name);
      DtrumCollectorService.addStringProperty(dtrum, actionId, 'errorMessage', event.error?.message);
      DtrumCollectorService.addStringProperty(dtrum, actionId, 'errorSeverity', event.error?.severity);
      dtrum.reportCustomError(event.error?.name + ' (' + actionName + ')', event.error?.message + ' (' + event.error?.errorType + ' , severity:' + event.error?.severity + ')', null, true);
      dtrum.leaveAction(actionId);
    }


  }

  private static addStringProperty<T>(dtrum: DtrumApi, actionId: number, propertyName: string, propertyValue: string) {
    dtrum.addActionProperties(actionId, undefined, undefined, {
      [propertyName.toLocaleLowerCase()]: propertyValue,
    });
  }

  private static addNumberProperty<T>(dtrum: DtrumApi, actionId: number, propertyName: string, propertyValue: number) {
    dtrum.addActionProperties(actionId, undefined, undefined, undefined, {
      [propertyName.toLocaleLowerCase()]: propertyValue,
    });
  }

  private addSessionProperties(dtrum: DtrumApi) {
    this.userFacade.get().pipe(untilDestroyed(this)).subscribe((user: User) => {
      dtrum.identifyUser(user?.scimUserId || 'anonymous');
    });

    this.baseSiteService.getActive().pipe(untilDestroyed(this)).subscribe(baseSiteId => {
      DtrumCollectorService.addSessionProperty(dtrum, 'baseSiteId', baseSiteId);
    });

  }

  private static isScriptError(event) {
    return !!event.error?.stack;

  }

  private static addSessionProperty(dtrum: DtrumApi, propertyName: string, propertyValue: string) {
    dtrum.sendSessionProperties(undefined, undefined, {
      [propertyName]: {
        value: propertyValue,
        public: true
      }
    }, undefined);
  }
}
